import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { AlertTriangle, Clock3, CheckCircle2, Edit2, X, Save, Send, ChevronsUp, Flag, UserCheck } from "lucide-react";
import { fmtDateTime } from "../lib/datetime";

function fmtDuration(mins) {
  if (mins == null) return "—";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = Math.round(mins - h * 60);
  return `${h}h ${m}m`;
}

function badgeColour(mins) {
  if (mins == null) return { bg: "#f1f5f9", fg: "#334155" };
  if (mins <= 60) return { bg: "#dcfce7", fg: "#065f46" };
  return { bg: "#fee2e2", fg: "#b91c1c" };
}

export default function Callouts() {
  const { user } = useAuth();
  const canDispatch = ["admin", "contracts_manager", "supervisor"].includes(user?.role);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [alerts, setAlerts] = useState({}); // visit_id → alerts[]
  const [expanded, setExpanded] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/callouts"); setRows(data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const loadAlerts = async (vid) => {
    try {
      const { data } = await api.get(`/nrs/visit/${vid}/alerts`);
      setAlerts((prev) => ({ ...prev, [vid]: data }));
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const dispatch = async (vid) => {
    try {
      const { data } = await api.post(`/nrs/dispatch/${vid}`);
      setSuccess(`NRS alert sent to ${data.recipient_name || "on-call"} (${data.sent ? "email delivered" : "email " + (data.reason || "queued")})`);
      load(); loadAlerts(vid);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const escalate = async (vid) => {
    if (!window.confirm("Escalate this callout to the next on-call tier?")) return;
    try {
      const { data } = await api.post(`/nrs/escalate/${vid}`);
      setSuccess(`Escalated — alert sent to ${data.recipient_name || "next tier"} (${data.tier})`);
      load(); loadAlerts(vid);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markOnSite = async (vid) => {
    try {
      await api.post(`/visits/${vid}/on-site`);
      setSuccess("On-site marked. 60-minute clock stopped.");
      load();
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const stats = useMemo(() => {
    const measured = rows.filter((r) => r.response_minutes != null);
    const within = measured.filter((r) => r.response_minutes <= 60).length;
    const pct = measured.length ? Math.round((within / measured.length) * 100) : 100;
    const breaches = measured.filter((r) => r.response_minutes > 60);
    const pendingAck = rows.filter((r) => !r.acknowledged_at && !r.on_site_at).length;
    return {
      total: rows.length,
      measured: measured.length,
      within60: within,
      pct,
      breachCount: breaches.length,
      open: rows.filter((r) => r.status !== "Complete").length,
      pendingAck,
    };
  }, [rows]);

  const toggleExpand = (vid) => {
    if (expanded === vid) { setExpanded(null); return; }
    setExpanded(vid);
    if (!alerts[vid]) loadAlerts(vid);
  };

  return (
    <div data-testid="callouts-page" style={{ padding: "24px 24px 64px" }}>
      <header style={{ marginBottom: 20 }}>
        <div className="mono" style={{ color: "#ff5a00", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em" }}>NEWELL RESPONSE SYSTEM · 60-MIN KPI · STAGE 1 (EMAIL ALERT + ACK-WITH-ETA)</div>
        <h1 style={{ fontSize: 28, fontWeight: 900, margin: "4px 0 0" }}>Callouts</h1>
        <div style={{ color: "#64748b", marginTop: 4 }}>A callout is an out-of-hours visit. NRS reads the rota, alerts the on-call responder, captures their ETA, and confirms back to McCain — all automatically.</div>
      </header>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
        <Tile testid="kpi-pct" label="Within 60 min" value={`${stats.pct}%`} accent={stats.pct < 100 ? "#dc2626" : "#059669"} />
        <Tile testid="kpi-measured" label="Measured" value={`${stats.within60} / ${stats.measured}`} />
        <Tile testid="kpi-breaches" label="Breaches (>60 min)" value={stats.breachCount} accent={stats.breachCount > 0 ? "#dc2626" : "#0f172a"} />
        <Tile testid="kpi-pending-ack" label="Awaiting ack" value={stats.pendingAck} accent={stats.pendingAck > 0 ? "#dc2626" : "#0f172a"} />
        <Tile testid="kpi-open" label="Open callouts" value={stats.open} accent="#ff5a00" />
      </div>

      {error ? <div data-testid="callouts-error" className="card" style={{ padding: 12, marginBottom: 12, background: "#fef2f2", color: "#b91c1c", borderLeft: "4px solid #dc2626", display: "flex", gap: 8, alignItems: "center" }}><AlertTriangle size={16} /> {error} <button className="btn-ghost" onClick={() => setError("")} style={{ marginLeft: "auto" }}><X size={12} /></button></div> : null}
      {success ? <div data-testid="callouts-success" className="card" style={{ padding: 12, marginBottom: 12, background: "#ecfdf5", color: "#065f46", borderLeft: "4px solid #059669", display: "flex", gap: 8, alignItems: "center" }}><CheckCircle2 size={16} /> {success} <button className="btn-ghost" onClick={() => setSuccess("")} style={{ marginLeft: "auto" }}><X size={12} /></button></div> : null}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="responsive-table" data-testid="callouts-table">
          <thead>
            <tr>
              <th>Received</th>
              <th>Responder</th>
              <th>ETA</th>
              <th>On-site</th>
              <th>Response</th>
              <th>Structure</th>
              <th>Description</th>
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: "#64748b" }}>No callouts logged. Raise a visit from the Visits page and tick &quot;Out-of-hours / Callout&quot;.</td></tr>
            ) : rows.map((c) => {
              const badge = badgeColour(c.response_minutes);
              const isExpanded = expanded === c.id;
              const isOpen = c.status !== "Complete";
              return (
                <React.Fragment key={c.id}>
                  <tr data-testid={`callout-row-${c.id}`}>
                    <td data-label="Received" style={{ fontSize: 12 }}>{c.callout_received_at ? fmtDateTime(c.callout_received_at) : <span style={{ color: "#dc2626" }}>missing</span>}</td>
                    <td data-label="Responder">{c.responder_name ? <span style={{ fontWeight: 600 }}><UserCheck size={12} style={{ verticalAlign: "middle", marginRight: 4, color: "#059669" }} />{c.responder_name}</span> : <span style={{ color: "#dc2626", fontSize: 12 }}>awaiting ack</span>}</td>
                    <td data-label="ETA">{c.eta_minutes ? <span className="mono" style={{ background: "#0f172a", color: "white", padding: "2px 8px", borderRadius: 3, fontSize: 11 }}>~{c.eta_minutes} min</span> : <span style={{ color: "#94a3b8" }}>—</span>}</td>
                    <td data-label="On-site" style={{ fontSize: 12 }}>{c.on_site_at ? fmtDateTime(c.on_site_at) : <span style={{ color: "#dc2626" }}>not yet</span>}</td>
                    <td data-label="Response">
                      <span data-testid={`callout-response-${c.id}`} className="mono" style={{ background: badge.bg, color: badge.fg, padding: "2px 8px", borderRadius: 3, fontSize: 11, fontWeight: 700 }}>
                        <Clock3 size={11} style={{ marginRight: 4, verticalAlign: "middle" }} />{fmtDuration(c.response_minutes)}
                      </span>
                    </td>
                    <td data-label="Structure"><Link to={`/structures/${c.structure_id}`} style={{ color: "#0f172a", fontWeight: 600 }}>{c.structure_description || c.structure_id}</Link></td>
                    <td data-label="Description" style={{ fontSize: 12, color: "#334155", maxWidth: 240 }}>{c.callout_description || <span style={{ color: "#94a3b8" }}>—</span>}</td>
                    <td data-label="Actions" style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      {canDispatch && !c.acknowledged_at && isOpen ? (
                        <>
                          {!c.responder_user_id ? <button data-testid={`dispatch-${c.id}`} className="btn-ghost" onClick={() => dispatch(c.id)} title="Dispatch NRS alert"><Send size={12} style={{ marginRight: 4 }} />Dispatch</button> : null}
                          <button data-testid={`escalate-${c.id}`} className="btn-ghost" onClick={() => escalate(c.id)} title="Escalate to next tier" style={{ color: "#dc2626" }}><ChevronsUp size={12} style={{ marginRight: 4 }} />Escalate</button>
                        </>
                      ) : null}
                      {canDispatch && c.acknowledged_at && !c.on_site_at ? (
                        <button data-testid={`onsite-${c.id}`} className="btn-primary" onClick={() => markOnSite(c.id)} title="Mark responder on site"><Flag size={12} style={{ marginRight: 4 }} />On site</button>
                      ) : null}
                      <button data-testid={`expand-${c.id}`} className="btn-ghost" onClick={() => toggleExpand(c.id)} title="Show NRS detail">{isExpanded ? "Hide" : "Detail"}</button>
                      <button data-testid={`edit-callout-${c.id}`} className="btn-ghost" onClick={() => setEditing(c)}><Edit2 size={12} /></button>
                    </td>
                  </tr>
                  {isExpanded ? (
                    <tr>
                      <td colSpan={8} style={{ background: "#f8fafc", padding: 14 }}>
                        <NRSDetail visit={c} alerts={alerts[c.id]} />
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing ? (
        <EditCalloutModal
          callout={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setSuccess("Callout updated"); load(); }}
          onError={(msg) => setError(msg)}
        />
      ) : null}
    </div>
  );
}

function Tile({ label, value, accent, testid }) {
  return (
    <div data-testid={testid} className="card" style={{ padding: 16, borderTop: `3px solid ${accent || "#0f172a"}` }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", letterSpacing: "0.1em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 900, marginTop: 4 }}>{value}</div>
    </div>
  );
}

function NRSDetail({ visit, alerts }) {
  return (
    <div>
      <div className="mono" style={{ fontSize: 10, color: "#64748b", letterSpacing: "0.12em", marginBottom: 8 }}>NRS ALERT TRAIL · VISIT {visit.id.slice(0, 8)}</div>
      {!alerts ? <div style={{ color: "#64748b", fontSize: 12 }}>Loading…</div> : alerts.length === 0 ? (
        <div style={{ color: "#94a3b8", fontSize: 12 }}>No alerts dispatched yet. Click &quot;Dispatch&quot;.</div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "#64748b" }}>
              <th style={{ padding: "4px 8px" }}>Tier</th>
              <th style={{ padding: "4px 8px" }}>Recipient</th>
              <th style={{ padding: "4px 8px" }}>Sent</th>
              <th style={{ padding: "4px 8px" }}>Status</th>
              <th style={{ padding: "4px 8px" }}>ETA</th>
              <th style={{ padding: "4px 8px" }}>Email</th>
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) => (
              <tr key={a.id} data-testid={`nrs-alert-${a.id}`} style={{ borderTop: "1px solid #e2e8f0" }}>
                <td style={{ padding: "6px 8px" }}><span className="mono" style={{ background: tierColour(a.tier).bg, color: tierColour(a.tier).fg, padding: "2px 6px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>{(a.tier || "").toUpperCase()}</span></td>
                <td style={{ padding: "6px 8px" }}>{a.recipient_name}<br /><span className="mono" style={{ fontSize: 10, color: "#64748b" }}>{a.recipient_email}</span></td>
                <td style={{ padding: "6px 8px" }} className="mono">{a.sent_at ? fmtDateTime(a.sent_at) : "—"}</td>
                <td style={{ padding: "6px 8px" }}><StatusBadge s={a.status} /></td>
                <td style={{ padding: "6px 8px" }}>{a.eta_minutes ? `~${a.eta_minutes} min` : "—"}</td>
                <td style={{ padding: "6px 8px" }}>{a.email_sent ? <span style={{ color: "#059669" }}>delivered</span> : <span style={{ color: "#dc2626" }}>{a.email_reason || "not sent"}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function tierColour(t) {
  if (t === "primary") return { bg: "#dbeafe", fg: "#1e40af" };
  if (t === "backup") return { bg: "#fef3c7", fg: "#92400e" };
  if (t === "escalation") return { bg: "#fee2e2", fg: "#b91c1c" };
  return { bg: "#f1f5f9", fg: "#334155" };
}

function StatusBadge({ s }) {
  const map = {
    pending: { bg: "#fef3c7", fg: "#92400e" },
    acknowledged: { bg: "#dcfce7", fg: "#065f46" },
    escalated: { bg: "#fee2e2", fg: "#b91c1c" },
    cancelled: { bg: "#f1f5f9", fg: "#64748b" },
  };
  const c = map[s] || { bg: "#f1f5f9", fg: "#334155" };
  return <span className="mono" style={{ background: c.bg, color: c.fg, padding: "2px 6px", borderRadius: 3, fontSize: 10, fontWeight: 700, letterSpacing: "0.08em" }}>{(s || "").toUpperCase()}</span>;
}

function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function EditCalloutModal({ callout, onClose, onSaved, onError }) {
  const [form, setForm] = useState({
    callout_received_at: toLocalInput(callout.callout_received_at),
    on_site_at: toLocalInput(callout.on_site_at),
    callout_description: callout.callout_description || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        callout_received_at: form.callout_received_at ? new Date(form.callout_received_at).toISOString() : null,
        on_site_at: form.on_site_at ? new Date(form.on_site_at).toISOString() : null,
        callout_description: form.callout_description,
      };
      await api.patch(`/visits/${callout.id}`, payload);
      onSaved();
    } catch (e) { onError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 80 }}>
      <div data-testid="edit-callout-modal" className="card" style={{ background: "white", maxWidth: 520, width: "100%", padding: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontWeight: 900 }}>Edit callout</h2>
          <button data-testid="close-edit-callout" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <Field label="Received at">
          <input data-testid="edit-received-at" type="datetime-local" value={form.callout_received_at} onChange={(e) => setForm({ ...form, callout_received_at: e.target.value })} />
        </Field>
        <Field label="On-site at">
          <input data-testid="edit-onsite-at" type="datetime-local" value={form.on_site_at} onChange={(e) => setForm({ ...form, on_site_at: e.target.value })} />
        </Field>
        <Field label="Description">
          <input data-testid="edit-callout-desc" type="text" value={form.callout_description} onChange={(e) => setForm({ ...form, callout_description: e.target.value })} />
        </Field>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button data-testid="save-callout" className="btn-primary" onClick={save} disabled={busy}><Save size={14} style={{ marginRight: 6, verticalAlign: "middle" }} />{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block", marginTop: 8 }}>
      <div className="mono" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>{label}</div>
      {children}
    </label>
  );
}
